import Phaser from 'phaser';
import {
  GAME_WIDTH,
  GAME_HEIGHT,
  FAMILY_COLORS,
  FAMILY_LABELS,
  Family,
  Tier,
  TIER_NAMES,
  SPLINTER_OVERCHARGE_COST,
  MAX_OVERCHARGE,
  OVERCHARGE_PER_MERGE,
  OVERCHARGE_PER_CASCADE_STEP,
  DECAY_BASE_THRESHOLD,
  DECAY_MIN_THRESHOLD,
  MAX_ACTIVE_FAMILIES_PER_RUN,
} from '../config';
import { MODIFIER_POOL, MODIFIERS_PER_RUN } from '../data/modifiers';
import {
  ACCENT_CYAN,
  ACCENT_CYAN_CSS,
  TEXT_PRIMARY_CSS,
  TEXT_SECONDARY_CSS,
  TEXT_TERTIARY_CSS,
} from '../ui/theme';

// Design tokens: design/handoff/DESIGN_TOKENS.md (single source of truth).

/**
 * HelpScene: Comprehensive "How to Play" screen with scrollable content,
 * actual element sprites, and up-to-date mechanic descriptions.
 */
export class HelpScene extends Phaser.Scene {
  private returnTo: string = 'Menu';
  private scrollContainer: Phaser.GameObjects.Container | null = null;
  private scrollOffset: number = 0;
  private maxScroll: number = 0;
  private lastPointerY: number = 0;

  constructor() {
    super({ key: 'Help' });
  }

  init(data: { returnTo?: string }): void {
    this.returnTo = data?.returnTo || 'Menu';
  }

  create(): void {
    this.scrollOffset = 0;
    this.maxScroll = 0;

    // Dark background
    this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x050507);

    this.mountScanline();
    this.events.on(Phaser.Scenes.Events.SHUTDOWN, this.unmountScanline, this);
    this.events.on(Phaser.Scenes.Events.DESTROY, this.unmountScanline, this);

    // Back button (top-left)
    this.createBackButton();

    // Header
    this.add.text(GAME_WIDTH / 2, 60, 'HOW TO PLAY', {
      fontFamily: 'Inter',
      fontSize: '28px',
      fontStyle: 'bold',
      color: TEXT_PRIMARY_CSS,
      align: 'center',
    }).setOrigin(0.5, 0);

    // Scrollable content container
    this.scrollContainer = this.add.container(GAME_WIDTH / 2, 120);

    let y = 0;

    // --- GOAL ---
    y = this.addSection('GOAL',
      'Merge elements to reach the highest score before you run out of moves. The game ends when no merges are possible anywhere on the board.',
      y);

    // --- MERGE ---
    y = this.addSection('MERGE',
      'Drag an element onto a matching element (same family and tier) to combine them into the next tier. You cannot move elements to empty cells — merging is your only action.',
      y);

    // --- SPAWNS ---
    y = this.addSection('SPAWNS',
      'After each merge, new fragments appear on the board. As your score rises, more spawn at once: 1 at first, then 2 (at 500 pts), then 3 (at 2000 pts). The board fills faster over time — plan ahead!',
      y);

    // --- CHAINS ---
    y = this.addSection('CHAINS',
      'When a merge result lands next to another matching element, they automatically cascade! Each chain step earns a multiplier: x1.5, x2.0, x2.5, and so on. Set up chains for big points.',
      y);

    // --- OVERCHARGE & SPLINTER ---
    y = this.addSection('OVERCHARGE & SPLINTER',
      `Long-press (hold) any Crystal-tier or higher element to splinter it into 2 same-family fragments of the tier below.\n\nSplintering costs ${SPLINTER_OVERCHARGE_COST} Overcharge from the bar above the grid. You build Overcharge by playing well: every merge grants ${OVERCHARGE_PER_MERGE} charge, and every chain cascade grants ${OVERCHARGE_PER_CASCADE_STEP} (so chaining is by far the fastest way to refill). Max ${MAX_OVERCHARGE}.\n\nThe bar turns purple once you have enough to splinter. Splinter is never gated by real-world timers — only your skill on the board.\n\nCharged fragments: splintered fragments land "charged" — if one lands adjacent to a same-family piece, it sparks a cascade chain just like a merge result. Aim for an adjacent match to turn a single splinter into a combo.`,
      y);

    // --- RUN MODIFIERS ---
    const modifierList = MODIFIER_POOL
      .map((m) => `• ${m.name} (${m.tone}) — ${m.description}`)
      .join('\n');
    y = this.addSection('RUN MODIFIERS',
      `Every fresh run rolls ${MODIFIERS_PER_RUN} modifiers from the pool below — shown in the banner at run start and in the gold badges under the Overcharge bar. Some make the run harder, others give you an edge. The mix is different every time, so no two runs play the same.\n\n${modifierList}`,
      y);

    // --- CORRUPTION & HEALING ---
    y = this.addSection('CORRUPTION & HEALING',
      `Elements age with each merge. When an element sits on the board too long (${DECAY_BASE_THRESHOLD} merges early on, as few as ${DECAY_MIN_THRESHOLD} at high scores), it becomes corrupted — marked with a red X. Corrupted elements are unmergeable blockers.\n\nA pulsing red ring warns you when an element is close to corrupting. Splinter an adjacent element to clear nearby corruption (splash damage).\n\nPositional healing: when you merge, all 8-direction neighbors of the result that share its family get -1 age — a green pulse confirms it. Tight, family-focused play actively pushes back the decay spiral.`,
      y);

    // --- DRAFT ---
    y = this.addSection('FAMILY DRAFT',
      `Each run is capped at ${MAX_ACTIVE_FAMILIES_PER_RUN} active families. Once you have unlocked more than that, the start of every run shows a draft screen — pick which families to bring. Drafting is the heart of the meta game: matchups against new families unlock as you progress.`,
      y);

    // --- WILD ELEMENTS ---
    y = this.addSection('WILD ELEMENTS', '', y);
    y = this.addWildDisplay(y);

    // --- ELEMENTS ---
    y = this.addSection('ELEMENTS',
      '8 families \u00d7 7 tiers = 56 unique elements to discover. Each family has a distinct shape and color. New families unlock as you reach higher tiers.',
      y);

    // --- FAMILIES with actual sprites ---
    y = this.addSectionTitle('FAMILIES', y);
    y = this.addFamilyGrid(y);

    // --- TIER PROGRESSION with sprites ---
    y = this.addSectionTitle('TIER PROGRESSION', y);
    y = this.addTierProgression(y);

    // --- FAMILY UNLOCKS ---
    y = this.addSection('FAMILY UNLOCKS',
      'Fire & Water: available from the start\nEarth: unlocked at Crystal tier\nAir & Lightning: unlocked at Gem tier\nShadow: unlocked at Core tier\nLight: unlocked at Primal tier\nVoid: unlocked at Eternal tier',
      y);

    // --- SCORING ---
    y = this.addSection('SCORING',
      'Points per merge (by result tier):\nFragment: 1  \u2022  Shard: 5  \u2022  Crystal: 25\nGem: 100  \u2022  Core: 500\nPrimal: 2,500  \u2022  Eternal: 10,000\n\nChain multipliers stack on top!',
      y);

    // --- GAME OVER ---
    y = this.addSection('GAME OVER',
      'The run ends when no valid merges exist on the board. There are no rescue spawns — if you run out of moves, it\'s over. Your score and discoveries are saved for next time.',
      y);

    // Scroll bounds: content height minus visible area (120 top + 80 bottom button)
    this.maxScroll = Math.max(0, y - (GAME_HEIGHT - 220));

    // "GOT IT" button
    this.createGotItButton();

    // Scroll: mouse wheel
    this.input.on('wheel', (_pointer: Phaser.Input.Pointer, _gameObjects: Phaser.GameObjects.GameObject[], _deltaX: number, deltaY: number) => {
      this.handleScroll(-deltaY * 0.5);
    });

    // Scroll: touch drag
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      this.lastPointerY = pointer.y;
    });

    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (pointer.isDown) {
        const delta = pointer.y - this.lastPointerY;
        this.handleScroll(delta);
        this.lastPointerY = pointer.y;
      }
    });
  }

  private scanlineEl: HTMLDivElement | null = null;

  private mountScanline(): void {
    if (typeof document === 'undefined' || this.scanlineEl) return;
    const el = document.createElement('div');
    el.className = 'crt-scanline';
    document.body.appendChild(el);
    this.scanlineEl = el;
  }

  private unmountScanline(): void {
    if (this.scanlineEl) {
      this.scanlineEl.remove();
      this.scanlineEl = null;
    }
  }

  // ========== CONTENT BUILDERS ==========

  private addSection(title: string, body: string, yOffset: number): number {
    if (!this.scrollContainer) return yOffset;
    const margin = 20;
    const contentWidth = GAME_WIDTH - 40;

    // Title
    const titleText = this.add.text(-GAME_WIDTH / 2 + margin, yOffset, title, {
      fontFamily: 'Inter',
      fontSize: '14px',
      fontStyle: 'bold',
      color: ACCENT_CYAN_CSS,
    });
    this.scrollContainer.add(titleText);

    if (!body) return yOffset + 24;

    // Body
    const bodyText = this.add.text(-GAME_WIDTH / 2 + margin, yOffset + 24, body, {
      fontFamily: 'Inter',
      fontSize: '13px',
      color: TEXT_SECONDARY_CSS,
      wordWrap: { width: contentWidth - 10 },
      lineSpacing: 4,
    });
    this.scrollContainer.add(bodyText);

    return yOffset + 24 + bodyText.height + 20;
  }

  private addSectionTitle(title: string, yOffset: number): number {
    if (!this.scrollContainer) return yOffset;
    const margin = 20;
    const titleText = this.add.text(-GAME_WIDTH / 2 + margin, yOffset, title, {
      fontFamily: 'Inter',
      fontSize: '14px',
      fontStyle: 'bold',
      color: ACCENT_CYAN_CSS,
    });
    this.scrollContainer.add(titleText);
    return yOffset + 24;
  }

  /**
   * Display all 8 families + Wild in a grid with actual element sprites and shape labels.
   */
  private addFamilyGrid(yOffset: number): number {
    if (!this.scrollContainer) return yOffset;

    const margin = 20;
    const colWidth = (GAME_WIDTH - margin * 2) / 3;
    const rowHeight = 56;

    const families: Array<{ family: Family; shape: string }> = [
      { family: Family.Fire, shape: 'Triangle' },
      { family: Family.Water, shape: 'Teardrop' },
      { family: Family.Earth, shape: 'Hexagon' },
      { family: Family.Air, shape: 'Swirl' },
      { family: Family.Lightning, shape: 'Bolt' },
      { family: Family.Shadow, shape: 'Crescent' },
      { family: Family.Light, shape: 'Star' },
      { family: Family.Void, shape: 'Octagon' },
      { family: Family.Wild, shape: 'Diamond' },
    ];

    const cols = 3;
    for (let i = 0; i < families.length; i++) {
      const { family, shape } = families[i];
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = -GAME_WIDTH / 2 + margin + col * colWidth + colWidth / 2;
      const y = yOffset + row * rowHeight;

      // Element sprite (fragment tier)
      const tierSuffix = 'fragment';
      const defId = `${family}_${tierSuffix}`;
      const sprite = this.add.image(x, y + 4, defId);
      sprite.setScale(1.5);
      this.scrollContainer!.add(sprite);

      // Family name
      const nameText = this.add.text(x, y + 24, FAMILY_LABELS[family], {
        fontFamily: 'Inter',
        fontSize: '11px',
        fontStyle: 'bold',
        color: '#' + FAMILY_COLORS[family].toString(16).padStart(6, '0'),
      }).setOrigin(0.5, 0);
      this.scrollContainer!.add(nameText);

      // Shape label
      const shapeText = this.add.text(x, y + 38, shape, {
        fontFamily: 'Inter',
        fontSize: '10px',
        color: TEXT_TERTIARY_CSS,
      }).setOrigin(0.5, 0);
      this.scrollContainer!.add(shapeText);
    }

    const totalRows = Math.ceil(families.length / cols);
    return yOffset + totalRows * rowHeight + 12;
  }

  /**
   * Show the 7 tiers using Fire family sprites as examples.
   */
  private addTierProgression(yOffset: number): number {
    if (!this.scrollContainer) return yOffset;

    const margin = 20;
    const tiers = [Tier.Fragment, Tier.Shard, Tier.Crystal, Tier.Gem, Tier.Core, Tier.Primal, Tier.Eternal];
    const spacing = (GAME_WIDTH - margin * 2) / tiers.length;

    // Sprites row
    for (let i = 0; i < tiers.length; i++) {
      const tier = tiers[i];
      const tierSuffix = TIER_NAMES[tier].toLowerCase();
      const defId = `fire_${tierSuffix}`;
      const x = -GAME_WIDTH / 2 + margin + i * spacing + spacing / 2;

      const sprite = this.add.image(x, yOffset + 8, defId);
      sprite.setScale(1.0);
      this.scrollContainer!.add(sprite);

      // Arrow between tiers
      if (i < tiers.length - 1) {
        const arrowX = x + spacing / 2;
        const arrow = this.add.text(arrowX, yOffset + 6, '\u2192', {
          fontFamily: 'Inter',
          fontSize: '12px',
          color: TEXT_TERTIARY_CSS,
        }).setOrigin(0.5, 0.5);
        this.scrollContainer!.add(arrow);
      }
    }

    // Tier names row
    for (let i = 0; i < tiers.length; i++) {
      const tier = tiers[i];
      const x = -GAME_WIDTH / 2 + margin + i * spacing + spacing / 2;
      // Abbreviate names to fit
      const shortNames: Record<number, string> = {
        [Tier.Fragment]: 'Frag',
        [Tier.Shard]: 'Shrd',
        [Tier.Crystal]: 'Crys',
        [Tier.Gem]: 'Gem',
        [Tier.Core]: 'Core',
        [Tier.Primal]: 'Prim',
        [Tier.Eternal]: 'Eter',
      };
      const label = this.add.text(x, yOffset + 30, shortNames[tier], {
        fontFamily: 'JetBrains Mono',
        fontSize: '9px',
        color: TEXT_SECONDARY_CSS,
      }).setOrigin(0.5, 0);
      this.scrollContainer!.add(label);
    }

    return yOffset + 56;
  }

  /**
   * Show the Wild element with explanation inline.
   */
  private addWildDisplay(yOffset: number): number {
    if (!this.scrollContainer) return yOffset;

    const margin = 20;

    // Wild sprite
    const sprite = this.add.image(-GAME_WIDTH / 2 + margin + 20, yOffset + 4, 'wild_fragment');
    sprite.setScale(1.8);
    this.scrollContainer.add(sprite);

    // Description next to sprite
    const desc = this.add.text(-GAME_WIDTH / 2 + margin + 46, yOffset - 8,
      'Rare purple diamond that appears after merges (5% chance). Can merge with any same-tier element — follows the non-wild family\'s progression. Two Wilds cannot merge with each other.',
      {
        fontFamily: 'Inter',
        fontSize: '13px',
        color: TEXT_SECONDARY_CSS,
        wordWrap: { width: GAME_WIDTH - margin * 2 - 50 },
        lineSpacing: 4,
      }
    );
    this.scrollContainer.add(desc);

    return yOffset + Math.max(40, desc.height + 8) + 16;
  }

  // ========== UI ELEMENTS ==========

  private createBackButton(): void {
    const buttonSize = 44;
    const padding = 12;

    const backBtn = this.add.rectangle(
      padding + buttonSize / 2,
      padding + buttonSize / 2,
      buttonSize, buttonSize,
      ACCENT_CYAN, 0.15
    );
    backBtn.setStrokeStyle(2, ACCENT_CYAN);
    backBtn.setInteractive({ useHandCursor: true });

    this.add.text(padding + buttonSize / 2, padding + buttonSize / 2, '\u2190', {
      fontFamily: 'Inter',
      fontSize: '20px',
      color: TEXT_PRIMARY_CSS,
    }).setOrigin(0.5, 0.5);

    backBtn.on('pointerdown', () => { this.scene.start(this.returnTo); });
    backBtn.on('pointerover', () => { backBtn.setFillStyle(ACCENT_CYAN, 0.25); });
    backBtn.on('pointerout', () => { backBtn.setFillStyle(ACCENT_CYAN, 0.15); });
  }

  private createGotItButton(): void {
    const buttonWidth = 120;
    const buttonHeight = 44;
    const buttonY = GAME_HEIGHT - 40;

    // Paired shadow (CTA glow) — lower-alpha duplicate behind the button.
    const glow = this.add.rectangle(GAME_WIDTH / 2, buttonY, buttonWidth + 18, buttonHeight + 14, ACCENT_CYAN, 0.18);
    glow.setDepth(199);

    const button = this.add.rectangle(GAME_WIDTH / 2, buttonY, buttonWidth, buttonHeight, ACCENT_CYAN, 0.15);
    button.setStrokeStyle(2, ACCENT_CYAN);
    button.setInteractive({ useHandCursor: true });
    button.setDepth(200);

    const text = this.add.text(GAME_WIDTH / 2, buttonY, 'GOT IT', {
      fontFamily: 'Inter',
      fontSize: '14px',
      fontStyle: 'bold',
      color: TEXT_PRIMARY_CSS,
    }).setOrigin(0.5, 0.5).setDepth(201);

    button.on('pointerdown', () => { this.scene.start(this.returnTo); });
    button.on('pointerover', () => { button.setFillStyle(ACCENT_CYAN, 0.25); });
    button.on('pointerout', () => { button.setFillStyle(ACCENT_CYAN, 0.15); });
  }

  private handleScroll(delta: number): void {
    if (!this.scrollContainer) return;

    this.scrollOffset = Phaser.Math.Clamp(
      this.scrollOffset - delta,
      0,
      this.maxScroll
    );

    this.scrollContainer.setY(120 - this.scrollOffset);
  }
}
